
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Loader2, Sparkles } from 'lucide-react';
import { useLeadGenerator } from '@/contexts/ai-assistant/useLeadGenerator';
import { toast } from 'sonner';

interface LeadGeneratorProps {
  content: string;
  currentLead?: string;
  onSelectLead: (lead: string) => void;
  onClose: () => void;
}

const LeadGenerator: React.FC<LeadGeneratorProps> = ({ 
  content, 
  currentLead,
  onSelectLead, 
  onClose 
}) => {
  const [leadOptions, setLeadOptions] = useState<any[]>([]);
  const [selectedLead, setSelectedLead] = useState<string | null>(null);
  const { generateLeadParagraph, isProcessing } = useLeadGenerator();

  useEffect(() => {
    if (content) {
      handleGenerateLeads();
    }
  }, []);

  const handleGenerateLeads = async () => {
    const response = await generateLeadParagraph(content);
    
    if (response.success && response.data) {
      try {
        // Handle different possible response formats
        let leads;
        if (Array.isArray(response.data)) {
          leads = response.data;
        } else if (typeof response.data === 'string') {
          // Try to parse as JSON if it's a string
          try {
            leads = JSON.parse(response.data);
          } catch {
            // If it's not valid JSON, treat it as a single lead
            leads = [{ text: response.data, style: 'default' }];
          }
        } else if (typeof response.data === 'object') {
          // If it's already an object with leads
          leads = response.data.leads || [response.data];
        }
        
        if (Array.isArray(leads) && leads.length > 0) {
          setLeadOptions(leads);
        } else {
          toast.error("Received invalid leads format from AI service");
        }
      } catch (error) {
        console.error("Error parsing leads:", error);
        toast.error("Failed to parse AI response");
      }
    }
  };

  const handleSelectLead = (lead: any) => {
    const leadText = typeof lead === 'string' ? lead : lead.text;
    setSelectedLead(leadText);
  };

  const handleApplyLead = () => {
    if (selectedLead) {
      onSelectLead(selectedLead);
      onClose();
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Sparkles className="w-5 h-5 mr-2 text-yellow-500" />
          Lead Paragraph Generator
        </CardTitle>
        <CardDescription>
          Choose a lead paragraph for your article or generate new options
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {isProcessing ? (
          <div className="flex flex-col items-center justify-center py-10">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              Generating lead paragraphs based on your article content...
            </p>
          </div>
        ) : leadOptions.length > 0 ? (
          <div className="space-y-4">
            {leadOptions.map((lead, index) => {
              const leadText = typeof lead === 'string' ? lead : lead.text;
              const leadStyle = typeof lead === 'object' ? lead.style : 'default';
              
              return (
                <div 
                  key={index}
                  className={`p-4 rounded-md border cursor-pointer transition-colors
                    ${selectedLead === leadText 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'}`}
                  onClick={() => handleSelectLead(lead)}
                >
                  <div className="mb-1 flex justify-between">
                    <span className="text-sm text-muted-foreground capitalize">
                      {leadStyle || 'Option'} {index + 1}
                    </span>
                  </div>
                  <p className="text-foreground">{leadText}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              No lead paragraphs generated yet. 
              {content.trim().length === 0 && 
                " Add some content to your article first."}
            </p>
            <Button
              onClick={handleGenerateLeads}
              disabled={content.trim().length === 0 || isProcessing}
            >
              Generate Lead Paragraphs
            </Button>
          </div>
        )}
      </CardContent>
      
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <div className="space-x-2">
          <Button 
            variant="outline" 
            onClick={handleGenerateLeads}
            disabled={isProcessing || content.trim().length === 0}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate New
              </>
            )}
          </Button>
          <Button 
            onClick={handleApplyLead} 
            disabled={!selectedLead}
          >
            Apply Selected
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default LeadGenerator;
